import * as R from 'ramda';
import { connect } from 'react-redux';
import { Form } from 'antd';
import { withHandlers, withProps, withStateHandlers, lifecycle, withState } from 'recompose';

import RequestForm from '../../components/request-form';

import {
  debounce,
  filterUsers,
  validateFiles,
  getOrgSettingId,
  getCurrentUserId,
  formAttachmentsList,
  rejectedFilesErrors,
  requestSettingToOptions,
  checkIfCurrentUserSalesAgent,
} from '../../utils';


import { debounceTime } from '../constants';
import { fetchContacts, fetchProjects } from '../../api/apiko-api';

import { fetchUsers } from '../../redux/thunk/users';
import { getRequestSettings } from '../../redux/selectors';
import { handleRequestCreate, fetchRequests } from '../../redux/thunk/requests';

const debouncedContactsFetch = debounce(fetchContacts, debounceTime);

const mapStateToProps = ({
  users: { items },
  ...state
}) => ({
  users: filterUsers(items),
  ...getRequestSettings(state),
  isCurrentUserSalesAgent: checkIfCurrentUserSalesAgent(items),
  isLoading: state.requests.isCreating || state.requests.isUpdating,
});

export const AddNewRequestModal = Form.create()(R.compose(
  connect(
    mapStateToProps,
    {
      handleRequestCreate, fetchRequests, fetchUsers,
    },
  ),
  withProps(() => ({
    currentUserId: getCurrentUserId(),
    title: 'Add new Request',
    withAddContact: true,
    isEdit: false,
    name: 'file',
  })),
  withState('projects', 'setProjects', []),
  withState('isFetchingProjects', 'setIsFetchingProjects', true),
  lifecycle({
    componentDidMount() {
      fetchProjects()
        .then((projects) => {
          this.props.setProjects(projects);
          this.props.setIsFetchingProjects(false);
        });

      if (!this.props.users) {
        this.props.fetchUsers();
      }
    },
  }),
  withStateHandlers(
    ({
      currentUserId,
      requestStatuses,
      requestSources,
      requestLabels,
      isCurrentUserSalesAgent,
      initialRequestSource = requestSettingToOptions(requestSources).find(({ value }) => value === 'Upwork'),
      initialRequestStatus = requestSettingToOptions(requestStatuses).find(({ value }) => value === 'Inbox'),
      initialRequestLabels = [],
      initialSelectedContact = undefined,
      initialSelectedProject = undefined,
      initialSelectedUsers = [...(isCurrentUserSalesAgent ? [currentUserId] : [])],
      initialContacts = [],
      initialFiles = [],
    }) => ({
      requestSourcesOptions: requestSettingToOptions(requestSources),
      requestStatusesOptions: requestSettingToOptions(requestStatuses),
      requestLabelsOptions: requestSettingToOptions(requestLabels),
      selectedRequestSource: initialRequestSource,
      selectedRequestStatus: initialRequestStatus,
      selectedRequestLabels: initialRequestLabels,
      selectedContacts: initialSelectedContact,
      selectedProject: initialSelectedProject,
      selectedUsers: initialSelectedUsers,
      searchedContacts: initialContacts,
      uploadedFiles: initialFiles,
      attachments: initialFiles,
      isFetchingContacts: false,
      isStatusPopUpVisible: false,
      isContactFormOpen: false,
    }),
    {
      setSelectedRequestSource: ({ requestSourcesOptions }) => ({ _id }) => ({
        selectedRequestSource: R.find(R.propEq('_id', _id), requestSourcesOptions),
      }),
      setSelectedRequestStatus: ({ requestStatusesOptions }) => ({ _id }) => ({
        selectedRequestStatus: R.find(R.propEq('_id', _id), requestStatusesOptions),
      }),
      setSelectedRequestLabels: () => selectedLabels => ({
        selectedRequestLabels: selectedLabels,
      }),
      setSelectedContacts: () => value => ({
        selectedContacts: value,
      }),
      setSelectedUsers: () => value => ({
        selectedUsers: value,
      }),
      setSelectedProject: () => projectId => ({
        selectedProject: projectId,
      }),
      isFetchingContactsData: () => value => ({
        isFetchingContacts: value,
      }),
      setContactsData: () => (contacts = []) => ({
        searchedContacts: contacts,
      }),
      setUploadedFiles: () => (files, attachments) => ({
        uploadedFiles: files,
        attachments,
      }),
      setContactFormOpen: () => value => ({
        isContactFormOpen: value,
      }),
      setIsStatusPopUpVisible: () => value => ({
        isStatusPopUpVisible: value,
      }),
    },
  ),
  withHandlers({
    onToggleContactForm: ({ setContactFormOpen, isContactFormOpen }) => () => {
      setContactFormOpen(!isContactFormOpen);
    },
    handleContactSearch: props => (value) => {
      if (value.length > 1) {
        props.isFetchingContactsData(true);
        debouncedContactsFetch({ searchTerm: value, simpleList: true })
          .then(({ data }) => {
            props.setContactsData(data.contacts);
            props.isFetchingContactsData(false);
          });
      } else props.setContactsData();
    },
    onSubmitForm: ({
      selectedRequestSource,
      selectedRequestStatus,
      selectedRequestLabels,
      selectedContacts,
      selectedProject,
      selectedUsers,
      uploadedFiles,
      form,
      ...actions
    }) => (event) => {
      event.preventDefault();
      form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          const data = new FormData();
          data.append('contacts', JSON.stringify(R.pluck('key', selectedContacts)));
          data.append('subject', values.subject);
          data.append('description', values.description);
          data.append('requestDate', values.requestDate);
          data.append('source', selectedRequestSource._id);
          data.append('status', selectedRequestStatus._id);
          data.append(
            'labels',
            JSON.stringify(selectedRequestLabels.map(label => label._id)),
          );

          if (selectedProject) {
            data.append('project', selectedProject);
          }

          uploadedFiles.map(file => (
            data.append('attachment', file)
          ));

          if (!R.isEmpty(selectedUsers)) {
            data.append('assignees', JSON.stringify(selectedUsers));
          }

          actions.handleRequestCreate(data);
        } else {
          console.error(err);
        }
      });
    },

    openFilePicker: () => (event) => {
      // activate file input by clicking on it
      event.target.nextElementSibling.click();
    },
    onAttachmentsAdd: props => (event) => {
      const files = Array.from(event.target.files);

      const { acceptedFiles, rejectedFiles } = validateFiles(files);

      props.setUploadedFiles(
        [...props.uploadedFiles, ...acceptedFiles],
        [...props.attachments, ...formAttachmentsList(acceptedFiles)],
      );

      rejectedFilesErrors(rejectedFiles);

      // reset input
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    onAttachmentRemove: props => (_id) => {
      const removedFileName = _id.split('-').slice(1)[0];

      props.setUploadedFiles(
        props.uploadedFiles.filter(i => i.name !== removedFileName),
        props.attachments.filter(i => i._id !== _id),
      );
    },
    onDrop: props => (files, rejectedFiles) => {
      props.setUploadedFiles(
        [...props.uploadedFiles, ...files],
        [...props.attachments, ...formAttachmentsList(files)],
      );

      rejectedFilesErrors(rejectedFiles);
    },

    handleChangeStatus: ({
      requestStatusesOptions,
      setIsStatusPopUpVisible,
      setSelectedRequestStatus,
    }) => (requestStatus) => {
      const customerStatusId = getOrgSettingId(requestStatusesOptions, 'Won');

      if (requestStatus._id === customerStatusId && requestStatus.label) {
        return setIsStatusPopUpVisible(true);
      }

      setIsStatusPopUpVisible(false);

      return setSelectedRequestStatus(requestStatus);
    },
    handleChangeLabels: ({ setSelectedRequestLabels }) => (requestLabels) => {
      const labels = !R.isNil(requestLabels) ? requestLabels : [];

      return setSelectedRequestLabels(labels);
    },
    onConfirmStatusChange: ({
      requestStatusesOptions,
      setIsStatusPopUpVisible,
      setSelectedRequestStatus,
    }) => () => {
      setIsStatusPopUpVisible(false);
      return setSelectedRequestStatus({
        _id: getOrgSettingId(requestStatusesOptions, 'Won'),
      });
    },
    onCancelStatusChange: ({ setIsStatusPopUpVisible }) => () => {
      setIsStatusPopUpVisible(false);
    },
  }),
)(RequestForm));



// WEBPACK FOOTER //
// ./src/modals/add-new-request/index.js